import React, { createContext, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import fetch from "isomorphic-fetch";

const STOREFRONT_ENDPOINT = `https://${process.env.GATSBY_SHOPIFY_STORE_URL}/api/2024-04/graphql.json`;

const graphqlClient = async (query, variables = {}) => {
  const response = await fetch(STOREFRONT_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token":
        process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  const result = await response.json();

  if (result.errors) {
    console.error("GraphQL errors:", result.errors);
    throw new Error(
      `GraphQL Error: ${result.errors.map((e) => e.message).join(", ")}`,
    );
  }

  return result;
};

const defaultValues = {
  loading: false,
  addLineItem: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  clearCart: () => {},
  logCart: () => {},
  countItemsInCart: () => 0,
  cart: {
    id: "",
    lines: { edges: [] },
    checkoutUrl: "",
    totalQuantity: 0,
    cost: {
      subtotalAmount: { amount: "0", currencyCode: "EUR" },
      totalAmount: { amount: "0", currencyCode: "EUR" },
    },
  },
};

const StoreContext = createContext(defaultValues);

const isBrowser = typeof window !== "undefined";
const localStorageKey = "shopify_cart_id";

const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    totalQuantity
    checkoutUrl
    cost {
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              weight
              weightUnit
              priceV2 {
                amount
                currencyCode
              }
              selectedOptions {
                name
                value
              }
              product {
                title
                handle
              }
            }
          }
        }
      }
    }
  }
`;

const CREATE_CART_MUTATION = `
  mutation cartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      ...CartFragment
    }
  }
  ${CART_FRAGMENT}
`;

const ADD_LINES_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const REMOVE_LINES_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const UPDATE_LINES_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(defaultValues.cart);
  const [loading, setLoading] = useState(false);

  const setCartItem = (newCart) => {
    if (isBrowser && newCart?.id) {
      localStorage.setItem(localStorageKey, newCart.id);
    }
    setCart(newCart || defaultValues.cart);
  };

  const createCart = async () => {
    try {
      const result = await graphqlClient(CREATE_CART_MUTATION, {
        input: {
          buyerIdentity: {
            countryCode: "DE",
          },
        },
      });

      if (result.data?.cartCreate?.userErrors?.length > 0) {
        console.error(
          "Cart creation errors:",
          result.data.cartCreate.userErrors,
        );
        throw new Error("Failed to create cart");
      }

      return result.data.cartCreate.cart;
    } catch (error) {
      console.error("Error creating cart:", error);
      throw error;
    }
  };

  const fetchCart = async (cartId) => {
    try {
      const result = await graphqlClient(CART_QUERY, { id: cartId });
      return result.data?.cart;
    } catch (error) {
      console.error("Error fetching cart:", error);
      return null;
    }
  };

  useEffect(() => {
    const initializeCart = async () => {
      setLoading(true);

      const existingCartID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null;

      if (existingCartID && existingCartID !== "null") {
        try {
          console.info("Found existing Cart ID in localStorage. Fetching...");
          const existingCart = await fetchCart(existingCartID);

          if (existingCart) {
            console.info("Successfully loaded existing cart from Shopify");
            setCartItem(existingCart);
            setLoading(false);
            return;
          } else {
            console.info(
              "Cart not found on Shopify, removing from localStorage",
            );
            localStorage.removeItem(localStorageKey);
          }
        } catch (error) {
          console.error("Error loading existing cart:", error);
          localStorage.removeItem(localStorageKey);
        }
      }

      try {
        console.info("Creating new cart...");
        const newCart = await createCart();
        setCartItem(newCart);
        console.info("New cart created successfully");
      } catch (error) {
        console.error("Failed to create new cart:", error);
        setCart(defaultValues.cart);
      }

      setLoading(false);
    };

    initializeCart();
  }, []);

  const addLineItem = async (variantId, quantity, structureId = "") => {
    if (!cart.id) {
      console.error("No cart ID available");
      return;
    }

    setLoading(true);

    try {
      const variables = {
        cartId: cart.id,
        lines: [
          {
            merchandiseId: variantId,
            quantity: parseInt(quantity, 10),
            attributes: structureId
              ? [{ key: "structureId", value: structureId }]
              : [],
          },
        ],
      };

      const result = await graphqlClient(ADD_LINES_MUTATION, variables);

      if (result.data?.cartLinesAdd?.userErrors?.length > 0) {
        console.error(
          "Add line item errors:",
          result.data.cartLinesAdd.userErrors,
        );
        throw new Error("Failed to add item to cart");
      }

      setCartItem(result.data.cartLinesAdd.cart);
      console.info("Item added to cart successfully");
    } catch (error) {
      console.error("Error adding line item:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeLineItem = async (lineItemID) => {
    if (!cart.id) {
      console.error("No cart ID available");
      return;
    }

    setLoading(true);

    try {
      const variables = {
        cartId: cart.id,
        lineIds: [lineItemID],
      };

      const result = await graphqlClient(REMOVE_LINES_MUTATION, variables);

      if (result.data?.cartLinesRemove?.userErrors?.length > 0) {
        console.error(
          "Remove line item errors:",
          result.data.cartLinesRemove.userErrors,
        );
        throw new Error("Failed to remove item from cart");
      }

      setCartItem(result.data.cartLinesRemove.cart);
      console.info("Item removed from cart successfully");
    } catch (error) {
      console.error("Error removing line item:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLineItem = async (lineItemID, quantity) => {
    if (!cart.id) {
      console.error("No cart ID available");
      return;
    }

    setLoading(true);

    try {
      const variables = {
        cartId: cart.id,
        lines: [
          {
            id: lineItemID,
            quantity: parseInt(quantity, 10),
          },
        ],
      };

      const result = await graphqlClient(UPDATE_LINES_MUTATION, variables);

      if (result.data?.cartLinesUpdate?.userErrors?.length > 0) {
        console.error(
          "Update line item errors:",
          result.data.cartLinesUpdate.userErrors,
        );
        throw new Error("Failed to update item in cart");
      }

      setCartItem(result.data.cartLinesUpdate.cart);
      console.info("Item updated in cart successfully");
    } catch (error) {
      console.error("Error updating line item:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);

    try {
      const newCart = await createCart();
      setCartItem(newCart);
      console.info("Cart cleared successfully");
    } catch (error) {
      console.error("Error clearing cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateCartLanguage = async (newLanguage) => {
    console.info(`Updating cart language to: ${newLanguage}`);

    if (!cart.id || cart.lines.edges.length === 0) {
      setLoading(true);
      try {
        const newCart = await createCart();
        setCartItem(newCart);
        console.info("Cart language updated successfully (empty cart)");
      } catch (error) {
        console.error("Error updating cart language:", error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    try {
      const newCart = await createCart();

      for (const edge of cart.lines.edges) {
        const line = edge.node;
        const variantId = line.merchandise.id;
        const quantity = line.quantity;

        const structureId =
          line.attributes.find((attr) => attr.key === "structureId")?.value ||
          "";

        const variables = {
          cartId: newCart.id,
          lines: [
            {
              merchandiseId: variantId,
              quantity: quantity,
              attributes: structureId
                ? [{ key: "structureId", value: structureId }]
                : [],
            },
          ],
        };

        const result = await graphqlClient(ADD_LINES_MUTATION, variables);

        if (result.data?.cartLinesAdd?.userErrors?.length > 0) {
          console.error(
            "Error transferring item to new cart:",
            result.data.cartLinesAdd.userErrors,
          );
        } else {
          Object.assign(newCart, result.data.cartLinesAdd.cart);
        }
      }

      setCartItem(newCart);
      console.info("Cart language updated successfully with items preserved");
    } catch (error) {
      console.error("Error updating cart language:", error);
    } finally {
      setLoading(false);
    }
  };

  const syncCartLanguage = async (currentLanguage) => {
    console.info(
      `Syncing cart language with current context: ${currentLanguage}`,
    );

    if (cart.id && currentLanguage) {
      console.info(`Cart exists, would sync to language: ${currentLanguage}`);
    }
  };

  const logCart = () => {
    console.log("Current cart:", cart);
    console.log("Cart lines:", cart.lines.edges);
  };

  // Count total items in cart
  const countItemsInCart = () => {
    return cart.totalQuantity || cart.lines.edges.length;
  };

  return (
    <StoreContext.Provider
      value={{
        addLineItem,
        removeLineItem,
        updateLineItem,
        countItemsInCart,
        logCart,
        clearCart,
        updateCartLanguage,
        syncCartLanguage,
        cart,
        loading,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

StoreProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useStore = () => {
  const context = useContext(StoreContext);

  if (context === undefined) {
    throw new Error("useStore must be used within StoreContext");
  }

  return context;
};

export default useStore;
